
import { useMutation } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const useAddGuests = (
  ensureAuthenticatedUser: () => Promise<string>
) => {
  const { toast } = useToast();

  return useMutation({
    mutationFn: async ({ eventId, guestIds }: { eventId: string; guestIds: string[] }) => {
      await ensureAuthenticatedUser();

      const { data: existingGuests } = await supabase
        .from('event_attendee')
        .select('user_id')
        .eq('event_id', eventId)
        .in('user_id', guestIds);

      const existingGuestIds = new Set(existingGuests?.map(g => g.user_id) || []);
      const newGuestIds = guestIds.filter(id => !existingGuestIds.has(id));

      if (newGuestIds.length === 0) {
        toast({
          title: "Info",
          description: "Selected guests are already invited",
        });
        return;
      }

      const attendeeRecords = newGuestIds.map(guestId => ({
        event_id: eventId,
        user_id: guestId,
        rsvp_status: 'invited' as const
      }));

      const { error } = await supabase
        .from('event_attendee')
        .insert(attendeeRecords);

      if (error) throw error;

      return { addedCount: newGuestIds.length, totalSelected: guestIds.length };
    },
    onSuccess: (result) => {
      if (result) {
        toast({
          title: "Success",
          description: `Successfully invited guests`,
        });
      }
    },
    onError: (error: any) => {
      console.error('Error adding guests:', error);
      toast({
        title: "Error",
        description: "Failed to add guests. Please try again.",
        variant: "destructive",
      });
    }
  });
};
